// BoardStep.js - Board/Curriculum selection step
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../i18n/useTranslation';
import { getBoardsWithMetadata, DEFAULT_BOARD } from '../../constants/boards';

// Get boards from shared constants
const BOARDS = getBoardsWithMetadata();

const BoardStep = ({
  selectedBoard = '',
  onBoardChange,
  onNext,
  onBack,
}) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  // Auto-select default board if none is provided
  const initialBoard = selectedBoard || DEFAULT_BOARD;
  const [board, setBoard] = useState(initialBoard);

  // On mount, if no board was passed, notify parent of default selection
  useEffect(() => {
    if (!selectedBoard && onBoardChange) {
      onBoardChange(DEFAULT_BOARD);
    }
  }, []);

  useEffect(() => {
    if (selectedBoard) {
      setBoard(selectedBoard);
    }
  }, [selectedBoard]);

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#2563eb',
    accentLight: isDark ? '#2563eb30' : '#2563eb15',
    border: isDark ? '#475569' : '#e2e8f0',
    selectedBorder: '#2563eb',
    selectedBg: isDark ? '#2563eb25' : '#2563eb10',
  };

  const selectBoard = (boardId) => {
    setBoard(boardId);
    onBoardChange?.(boardId);
  };

  const isValid = !!board;

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Title */}
        <Text style={[styles.title, { color: colors.text }]}>
          {t('Select your curriculum')}
        </Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          {t('Choose the board that matches your school')}
        </Text>

        {/* Board Options - Fixed height scrollable container */}
        <View style={styles.boardsWrapper}>
          <ScrollView
            style={styles.boardsScroll}
            contentContainerStyle={styles.boardsContainer}
            showsVerticalScrollIndicator={true}
          >
            {BOARDS.map((b) => {
              const isSelected = board === b.id;
              return (
                <TouchableOpacity
                  key={b.id}
                  style={[
                    styles.boardCard,
                    {
                      backgroundColor: isSelected ? colors.selectedBg : colors.bg,
                      borderColor: isSelected ? colors.selectedBorder : colors.border,
                    },
                  ]}
                  onPress={() => selectBoard(b.id)}
                  activeOpacity={0.7}
                >
                  {/* Radio indicator */}
                  <View
                    style={[
                      styles.radio,
                      {
                        borderColor: isSelected ? colors.accent : colors.border,
                        backgroundColor: isSelected ? colors.accent : 'transparent',
                      },
                    ]}
                  >
                    {isSelected && <View style={styles.radioInner} />}
                  </View>

                  {/* Icon */}
                  <View
                    style={[
                      styles.iconWrapper,
                      {
                        backgroundColor: isSelected ? colors.accentLight : colors.bg,
                      },
                    ]}
                  >
                    <MaterialCommunityIcons
                      name={b.icon}
                      size={24}
                      color={isSelected ? colors.accent : colors.textSecondary}
                    />
                  </View>

                  {/* Label and description */}
                  <View style={styles.boardText}>
                    <View style={styles.labelRow}>
                      <Text
                        style={[
                          styles.boardLabel,
                          { color: isSelected ? colors.accent : colors.text },
                        ]}
                      >
                        {b.label}
                      </Text>
                      <View style={[styles.regionBadge, { backgroundColor: colors.accentLight }]}>
                        <Text style={[styles.regionText, { color: colors.accent }]}>
                          {t(b.region)}
                        </Text>
                      </View>
                    </View>
                    <Text
                      style={[styles.boardFullName, { color: colors.text }]}
                      numberOfLines={1}
                    >
                      {t(b.fullName)}
                    </Text>
                    <Text style={[styles.boardDescription, { color: colors.textSecondary }]}>
                      {t(b.description)}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        {/* Navigation Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.secondaryButton, { borderColor: colors.border }]}
            onPress={onBack}
            activeOpacity={0.7}
          >
            <MaterialCommunityIcons name="arrow-left" size={18} color={colors.text} />
            <Text style={[styles.secondaryButtonText, { color: colors.text }]}>{t('Back')}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.primaryButton,
              { backgroundColor: isValid ? colors.accent : colors.border },
            ]}
            onPress={onNext}
            activeOpacity={0.8}
            disabled={!isValid}
          >
            <Text style={[styles.primaryButtonText, { opacity: isValid ? 1 : 0.5 }]}>
              {t('Continue')}
            </Text>
            <MaterialCommunityIcons
              name="arrow-right"
              size={18}
              color="#fff"
              style={{ opacity: isValid ? 1 : 0.5 }}
            />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 16,
    padding: 28,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 24,
  },
  boardsWrapper: {
    height: 320,
    marginBottom: 24,
    borderRadius: 12,
    overflow: 'hidden',
  },
  boardsScroll: {
    flex: 1,
  },
  boardsContainer: {
    gap: 10,
    paddingRight: 4,
  },
  boardCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 12,
    borderWidth: 2,
    gap: 12,
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#fff',
  },
  iconWrapper: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
  boardText: {
    flex: 1,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  boardLabel: {
    fontSize: 16,
    fontWeight: '700',
  },
  regionBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  regionText: {
    fontSize: 10,
    fontWeight: '600',
  },
  boardFullName: {
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 2,
  },
  boardDescription: {
    fontSize: 12,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  secondaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  primaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    gap: 6,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});

export default BoardStep;
